import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import '../styles/AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Modal message
  const [messageModal, setMessageModal] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des utilisateurs');
      setLoading(false);
      if (err.response?.status === 403) {
        navigate('/');
      }
    }
  };

  const handleViewProducts = (userId) => {
    navigate(`/admin/products?seller=${userId}`);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => (u.id === userId ? { ...u, role: newRole } : u)));
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la mise à jour du rôle');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('confirms.deleteUser'))) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      alert(t('admin.users.deleteSuccess'));
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const openMessageModal = (user) => {
    setMessageModal(user);
    setMessageText('');
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !messageModal) return;
    setSendingMessage(true);
    try {
      const response = await api.post(`/admin/users/${messageModal.id}/message`, {
        message: messageText.trim()
      });
      setMessageModal(null);
      setMessageText('');
      // Proposer d'aller voir la conversation
      if (window.confirm(t('admin.users.messageSuccess'))) {
        navigate(`/my-messages?conversation=${response.data.conversationId}`);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error) return <div className="error">{error}</div>;

  // Détection de localisation suspecte
  const isSuspicious = (user) => {
    const country = (user.registration_country || '').toLowerCase();
    const loginCountry = (user.last_login_country || '').toLowerCase();
    const safeCountries = ['suisse', 'france', 'switzerland', 'local', ''];
    return (country && !safeCountries.includes(country)) || (loginCountry && !safeCountries.includes(loginCountry));
  };

  return (
    <div className="admin-users">
      <div className="page-header">
        <h1>{t('admin.users.title')}</h1>
        <button onClick={() => navigate('/admin')} className="back-btn">
          {t('common.backToDashboard')}
        </button>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>{t('common.id')}</th>
              <th>{t('admin.users.username')}</th>
              <th>{t('common.email')}</th>
              <th>{t('common.phone')}</th>
              <th>{t('admin.users.role')}</th>
              <th>Inscription</th>
              <th>Derniere connexion</th>
              <th>{t('admin.users.stripe')}</th>
              <th>{t('admin.users.registrationDate')}</th>
              <th>{t('common.message')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={isSuspicious(user) ? { background: '#fff3f3' } : {}}>
                <td>{user.id}</td>
                <td>
                  {user.username}
                  {isSuspicious(user) && <span style={{ marginLeft: 6, color: '#f44336', fontWeight: 'bold', fontSize: 16 }} title="Localisation suspecte">⚠</span>}
                </td>
                <td>{user.email}</td>
                <td>{user.phone || '-'}</td>
                <td>
                  <select
                    value={user.role || 'user'}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className={`role-badge ${user.role}`}
                    title="Modifier le rôle"
                  >
                    <option value="user">{t('admin.users.user')}</option>
                    <option value="pro">Vendeur pro</option>
                    <option value="admin">{t('admin.users.administrator')}</option>
                  </select>
                </td>
                <td>
                  {user.registration_country ? (
                    <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 'bold', color: isSuspicious(user) ? '#f44336' : '#333' }}>
                        {user.registration_city}, {user.registration_country}
                      </div>
                      <div style={{ color: '#888', fontSize: 11 }}>{user.registration_isp}</div>
                      <div style={{ color: '#aaa', fontSize: 10, fontFamily: 'monospace' }}>{user.registration_ip}</div>
                    </div>
                  ) : (
                    <span style={{ color: '#ccc', fontSize: 12 }}>-</span>
                  )}
                </td>
                <td>
                  {user.last_login_country ? (
                    <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 'bold', color: (user.last_login_country || '').toLowerCase() !== (user.registration_country || '').toLowerCase() && user.last_login_country ? '#ff9800' : '#333' }}>
                        {user.last_login_city}, {user.last_login_country}
                      </div>
                      <div style={{ color: '#aaa', fontSize: 10, fontFamily: 'monospace' }}>{user.last_login_ip}</div>
                      {user.last_login_at && <div style={{ color: '#888', fontSize: 10 }}>{formatDate(user.last_login_at)}</div>}
                    </div>
                  ) : (
                    <span style={{ color: '#ccc', fontSize: 12 }}>Jamais</span>
                  )}
                </td>
                <td>
                  <span className={`stripe-badge ${user.stripe_account_id ? 'active' : 'none'}`}>
                    {user.stripe_account_id ? t('admin.users.stripeActive') : '-'}
                  </span>
                </td>
                <td>{formatDate(user.created_at)}</td>
                <td>
                  {user.role !== 'admin' && (
                    <button
                      onClick={() => openMessageModal(user)}
                      className="message-btn"
                      title={t('admin.users.sendMessage', { name: user.username })}
                    >
                      💬
                    </button>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => handleViewProducts(user.id)}
                    className="view-btn"
                  >
                    {t('admin.users.viewProducts')}
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="delete-btn"
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal d'envoi de message */}
      {messageModal && (
        <div className="message-modal-overlay" onClick={() => setMessageModal(null)}>
          <div className="message-modal" onClick={e => e.stopPropagation()}>
            <div className="message-modal-header">
              <h3>{t('admin.users.messageToUser', { name: messageModal.username })}</h3>
              <button className="modal-close" onClick={() => setMessageModal(null)}>X</button>
            </div>
            <div className="message-modal-body">
              <textarea
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                placeholder={t('admin.users.messagePlaceholder')}
                rows="5"
                autoFocus
              />
            </div>
            <div className="message-modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setMessageModal(null)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={!messageText.trim() || sendingMessage}
              >
                {sendingMessage ? t('admin.users.sendingMessage') : t('common.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
